/**
 * VEL-2279 — Bundle PDP shopper UX on the native Simple Bundles form.
 * Does not stamp or rewrite _UDF1. Tax preflight stays in jafra-bundle-udf.js.
 *
 * - Cap quantity to kit inventory (data-jafra-bundle-max-qty)
 * - Sold Out when a visible Choice slot has no in-stock options
 * - Clear SB prefills on multi-choice dropdowns → "Please select an option"
 * - Keep Add to cart disabled until every visible Choice is selected
 * - Includes: show fixed always; Choice rows only after a pick; hide empty Infinite
 * - Update the Includes accordion when a Choice changes
 */
(function () {
  const FORM_SEL = 'form[action*="/cart/add"][data-jafra-bundle-pdp="true"]';
  const SB_ROOT_SEL = "#simple-bundles-io-options, #simple-bundles-options";
  let userTouched = false;

  function getForm() {
    return document.querySelector(FORM_SEL);
  }

  function getIncludesRoot() {
    return document.querySelector("[data-jafra-bundle-includes]");
  }

  function getSelects() {
    const root = document.querySelector(SB_ROOT_SEL);
    if (!root) return [];
    return Array.from(root.querySelectorAll("select")).filter((el) => {
      const name = String(el.name || "").toLowerCase();
      const id = String(el.id || "").toLowerCase();
      if (name === "id") return false;
      if (id === "product-select" || id.startsWith("product-select-")) return false;
      // Theme CSS hides Product 1..N rows — never treat as shopper Choice slots.
      if (/^product-\d+$/.test(id)) return false;
      return true;
    });
  }

  function isShopperVisible(select) {
    return Boolean(select.offsetParent);
  }

  /**
   * SB often auto-selects the first product. Shopper UX is empty until they choose.
   * Same idea as VEL-2183 limit-1: keep single-option / hidden Product N selected.
   */
  function clearSbPrefills() {
    if (userTouched) return;
    getSelects().forEach((select) => {
      const options = realOptions(select);
      if (options.length <= 1 || !isShopperVisible(select)) return;
      const emptyOption = select.querySelector('option[value=""]');
      if (emptyOption) select.value = "";
    });
  }

  /** properties[Selection 1] → "Selection 1" */
  function selectSlotName(select) {
    const raw = String(select?.name || "");
    const match = raw.match(/^properties\[(.+)\]$/i);
    return (match ? match[1] : raw).trim();
  }

  function slotKeyHandle(slotKey) {
    return String(slotKey || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function findSlotForSelect(select, map) {
    if (!Array.isArray(map) || !map.length) return null;
    const slotName = selectSlotName(select);
    if (slotName) {
      const byName = map.find(
        (slot) =>
          String(slot?.name || "").trim().toLowerCase() === slotName.toLowerCase(),
      );
      if (byName) return byName;
    }
    const visible = getSelects().filter(isShopperVisible);
    const index = visible.indexOf(select);
    if (index < 0) return null;
    return map.find((slot) => Number(slot?.index) === index) || map[index] || null;
  }

  function realOptions(select) {
    return Array.from(select.options).filter((option) => option.value);
  }

  function slotFullyOos(select) {
    const options = realOptions(select);
    if (!options.length) return true;
    return options.every((option) => option.disabled);
  }

  function getAddButton(form) {
    return (
      form?.querySelector("[data-add-to-cart]") ||
      form?.closest(".product-form")?.querySelector("[data-add-to-cart]")
    );
  }

  function applyMaxQty(form) {
    const qty = form.querySelector("[data-quantity-input]");
    const maxQty = form.getAttribute("data-jafra-bundle-max-qty");
    if (!qty || maxQty === null || maxQty === "") return;
    const max = parseInt(maxQty, 10);
    if (!Number.isFinite(max) || max < 1) return;
    qty.max = String(max);
    if (parseInt(qty.value, 10) > max) qty.value = String(max);
  }

  function setSoldOutLabel(button, soldOut) {
    const text = button.querySelector("[data-add-to-cart-text]");
    if (!text) return;
    if (soldOut) {
      if (!text.dataset.jafraPdpOriginal) {
        text.dataset.jafraPdpOriginal = text.textContent;
      }
      text.textContent = window.theme?.strings?.soldOut || "Sold Out";
      return;
    }
    if (text.dataset.jafraPdpOriginal) {
      text.textContent = text.dataset.jafraPdpOriginal;
    }
  }

  function includesMap() {
    const el = document.querySelector("[data-jafra-bundle-includes-map]");
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function norm(s) {
    return String(s || "")
      .trim()
      .toLowerCase();
  }

  /**
   * SB Infinite selects often use "Product title - Variant".
   * The Includes map may store that full string, or productTitle + variantTitle,
   * or only the product title when both shades share one product.
   */
  function matchOption(slot, select) {
    if (!slot || !Array.isArray(slot.options)) return null;
    const value = String(select.value || "").trim();
    const label = String(select.options[select.selectedIndex]?.text || "").trim();
    const candidates = [value, label].filter(Boolean);
    if (!candidates.length) return null;

    const exact = (option, c) => {
      const title = String(option?.title || "").trim();
      const productTitle = String(option?.productTitle || option?.product_title || "").trim();
      const variantTitle = String(option?.variantTitle || option?.variant_title || "").trim();
      const combined =
        productTitle && variantTitle && variantTitle !== "Default Title"
          ? `${productTitle} - ${variantTitle}`
          : "";
      const pool = [title, productTitle, combined].filter(Boolean);
      return pool.some((p) => p === c || norm(p) === norm(c));
    };

    for (const c of candidates) {
      const hit = slot.options.find((option) => exact(option, c));
      if (hit) return hit;
    }

    // "Product - Shade" select vs product-only map title (unique product in slot).
    for (const c of candidates) {
      const prefixHits = slot.options.filter((option) => {
        const title = String(option?.title || "").trim();
        const productTitle = String(option?.productTitle || option?.product_title || title).trim();
        if (!productTitle) return false;
        return (
          c.startsWith(`${productTitle} - `) ||
          norm(c).startsWith(`${norm(productTitle)} - `)
        );
      });
      if (prefixHits.length === 1) return prefixHits[0];
      if (prefixHits.length > 1) {
        const shade = c.includes(" - ") ? c.slice(c.indexOf(" - ") + 3).trim() : "";
        if (!shade) continue;
        const byShade = prefixHits.find((option) => {
          const variantTitle = String(option?.variantTitle || option?.variant_title || "").trim();
          const title = String(option?.title || "").trim();
          return (
            norm(variantTitle) === norm(shade) ||
            norm(title) === norm(shade) ||
            norm(title).endsWith(norm(shade)) ||
            (title.includes(" - ") && norm(title.slice(title.indexOf(" - ") + 3)) === norm(shade))
          );
        });
        if (byShade) return byShade;
      }
    }

    return null;
  }

  function findWrapForSelect(select, visibleSelects) {
    const index = visibleSelects.indexOf(select);
    if (index < 0) return null;
    const map = includesMap();
    const slot = findSlotForSelect(select, map);
    const slotKey = selectSlotName(select);
    return (
      (slotKey &&
        document.querySelector(
          `[data-includes-bundle-slot-key="${CSS.escape(slotKeyHandle(slotKey))}"]`,
        )) ||
      document.querySelector(`[data-includes-bundle-slot="${index}"]`) ||
      (slot && Number.isFinite(Number(slot.index))
        ? document.querySelector(`[data-includes-bundle-slot="${slot.index}"]`)
        : null)
    );
  }

  function fillIncludesWrap(wrap, option, select) {
    if (!wrap || !option) return;
    const titleName = wrap.querySelector("[data-includes-bundle-slot-name]");
    const selectedLabel = String(select?.options?.[select.selectedIndex]?.text || "").trim();
    if (titleName) titleName.textContent = selectedLabel || option.title;
    const weight = wrap.querySelector("[data-includes-bundle-slot-title] .bundle__product__weight");
    if (weight && option.weight) weight.textContent = option.weight;

    const media = wrap.querySelector("[data-includes-bundle-slot-media]");
    if (media && option.image) {
      // Choice rows ship with image-fit/lazysizes for the first catalog option.
      // Only swapping img.src leaves data-src + wipe background on the old product,
      // so 2nd/3rd picks of the same SKU show a broken or wrong thumb.
      const url = option.image.startsWith("//") ? "https:" + option.image : option.image;
      const lazyBox = media.querySelector(".lazy-image");
      if (lazyBox) {
        lazyBox.style.backgroundImage = "none";
        lazyBox.style.paddingTop = "100%";
      }
      let img = media.querySelector("img");
      if (!img) {
        img = document.createElement("img");
        img.className = "fade-in";
        (lazyBox || media).appendChild(img);
      }
      img.classList.remove("lazyload", "lazyloading");
      img.classList.add("lazyloaded", "fade-in");
      [
        "data-src",
        "data-srcset",
        "data-widths",
        "data-sizes",
        "data-aspectratio",
      ].forEach((attr) => img.removeAttribute(attr));
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = url;
      img.alt = selectedLabel || option.title || "";
      img.style.opacity = "1";
    }

    const link = wrap.querySelector("[data-includes-bundle-slot-url]");
    if (link && option.url) link.setAttribute("href", option.url);

    const description = wrap.querySelector("[data-includes-bundle-slot-description]");
    if (description && option.description) description.innerHTML = option.description;
  }

  /**
   * Fixed rows always stay. Choice rows appear only when that select has a value.
   * Whole Includes block hides when there is nothing to show (Infinite, no picks yet).
   */
  function syncIncludesVisibility() {
    const root = getIncludesRoot();
    if (!root) return;

    const fixedCount =
      parseInt(root.getAttribute("data-jafra-bundle-includes-fixed") || "0", 10) || 0;
    const visibleSelects = getSelects().filter(isShopperVisible);
    const map = includesMap();

    root.querySelectorAll("[data-includes-bundle-choice]").forEach((wrap) => {
      wrap.hidden = true;
    });

    let filledChoices = 0;
    visibleSelects.forEach((select) => {
      const wrap = findWrapForSelect(select, visibleSelects);
      if (!wrap) return;
      const hasValue = Boolean(String(select.value || "").trim());
      if (!hasValue) {
        wrap.hidden = true;
        return;
      }
      const slot = findSlotForSelect(select, map);
      const option = matchOption(slot, select);
      if (!option) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      filledChoices += 1;
      fillIncludesWrap(wrap, option, select);
    });

    const total = fixedCount + filledChoices;
    const countEl = root.querySelector("[data-jafra-bundle-includes-count]");
    if (countEl) countEl.textContent = String(total);
    root.hidden = total < 1;
  }

  function sync() {
    const form = getForm();
    if (!form) return;

    applyMaxQty(form);

    const button = getAddButton(form);
    if (!button) return;

    const liquidSoldOut = form.getAttribute("data-jafra-sold-out") === "true";
    if (liquidSoldOut || button.hasAttribute("data-disabled-flag")) {
      applyMaxQty(form);
      return;
    }

    const visibleSelects = getSelects().filter(isShopperVisible);
    const oos = visibleSelects.some(slotFullyOos);
    if (oos) {
      button.setAttribute("disabled", "disabled");
      setSoldOutLabel(button, true);
      return;
    }
    setSoldOutLabel(button, false);

    const allFilled =
      visibleSelects.length === 0 || visibleSelects.every((select) => String(select.value || "").trim());

    if (!allFilled) {
      button.setAttribute("disabled", "disabled");
      return;
    }

    if (form.getAttribute("data-limit-1-per-product") === "true") {
      return;
    }

    button.removeAttribute("disabled");
  }

  document.addEventListener("change", (event) => {
    if (!event.target?.closest?.(SB_ROOT_SEL)) return;
    userTouched = true;
    syncIncludesVisibility();
    sync();
  });

  const start = () => {
    clearSbPrefills();
    sync();
    syncIncludesVisibility();
    let n = 0;
    const id = setInterval(() => {
      clearSbPrefills();
      sync();
      syncIncludesVisibility();
      if (++n >= 24) clearInterval(id);
    }, 250);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
